import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, ChevronRight, BookOpen } from "lucide-react";
import api from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then((r) => {
      const p = r.data;
      setPost(p);
      document.title = p.title;
      const desc = p.meta_description;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
      m.setAttribute("content", desc);
      let c = document.querySelector('link[rel="canonical"]');
      if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
      c.setAttribute("href", `${window.location.origin}/blog/${slug}`);
      injectSchema(p);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container mx-auto px-6 py-20 text-center" data-testid="blog-not-found">
      <h1 className="font-heading font-extrabold text-3xl mb-4 text-[#2C3E50]">Article introuvable</h1>
      <Link to="/blog" className="btn-primary">Retour au blog</Link>
    </div>
  );
  if (!post) return <div className="container mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">Chargement…</div>;

  return (
    <article className="animate-fade-up" data-testid="blog-post-page">
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 lg:px-10 pt-8 max-w-4xl">
        <nav className="text-xs text-[#6B7280] flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-[#2BD391]">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#2C3E50] font-semibold truncate max-w-md">{post.title}</span>
        </nav>
      </div>

      {/* Header */}
      <header className="container mx-auto px-6 lg:px-10 py-8 md:py-12 max-w-4xl">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#2BD391] mb-5">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour au blog
        </Link>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-4">
          <span className="px-2 py-0.5 bg-[#E8FBF3] text-[#1AA66C] rounded-full inline-flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> {post.category}
          </span>
          <Clock className="w-3 h-3 ml-1" /> {post.reading_time} min de lecture
          <span>· {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#2C3E50] leading-tight mb-4" data-testid="blog-h1">
          {post.h1}
        </h1>
        <p className="text-lg text-[#6B7280] leading-relaxed max-w-3xl">{post.excerpt}</p>
      </header>

      {/* Body */}
      <div className="container mx-auto px-6 lg:px-10 max-w-4xl pb-16">
        <div className="prose-clean" data-testid="blog-body">
          {post.sections.map((s, i) => (
            <section key={i} className="mb-10">
              <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#2C3E50] mb-5 leading-tight">
                {s.h2}
              </h2>
              <div className="space-y-4">
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="text-[#2C3E50] text-[15px] leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Liens internes contextuels */}
        {post.internal_links?.length > 0 && (
          <div className="my-10 bg-[#F7F9FB] rounded-3xl p-6 md:p-8 border border-[#E5E7EB]" data-testid="internal-links">
            <div className="eyebrow mb-3">À consulter également</div>
            <ul className="space-y-2">
              {post.internal_links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex items-center gap-2 text-sm font-semibold text-[#1AA66C] hover:underline">
                    <ArrowRight className="w-3.5 h-3.5" /> {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FAQ */}
        {post.faq?.length > 0 && (
          <section className="my-12" data-testid="blog-faq">
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#2C3E50] mb-6">Questions fréquentes</h2>
            <div className="space-y-3">
              {post.faq.map((item, i) => (
                <details key={i} className="group bg-[#F7F9FB] border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer p-5 flex items-start justify-between gap-4 font-semibold text-[#2C3E50] hover:bg-[#E8FBF3] transition-colors">
                    <span>{item.q}</span>
                    <ChevronRight className="w-4 h-4 shrink-0 mt-1 group-open:rotate-90 transition-transform text-[#2BD391]" />
                  </summary>
                  <div className="px-5 pb-5 text-sm text-[#5C6B7A] leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="my-12 bg-gradient-to-br from-[#2BD391] to-[#1AA66C] rounded-3xl p-8 md:p-12 text-center">
          <h2 className="font-heading font-extrabold text-white text-2xl md:text-3xl mb-3" style={{ color: "white" }}>
            Prêt à comparer plusieurs devis ?
          </h2>
          <p className="text-white/90 max-w-lg mx-auto mb-6 text-sm">
            Un formulaire unique, plusieurs devis auprès de nos entreprises partenaires. Gratuit et sans engagement.
          </p>
          <Link to="/devis" className="inline-flex items-center gap-2 bg-white text-[#1AA66C] hover:bg-[#1A2A3A] hover:text-white rounded-full px-8 py-4 font-bold text-sm uppercase tracking-wider transition-all" data-testid="blog-cta">
            Demander un devis gratuit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Note transparence */}
        <div className="my-8 bg-white border border-[#E5E7EB] rounded-2xl p-5 text-xs text-[#6B7280] leading-relaxed">
          <p>
            <strong className="text-[#2C3E50]">Note :</strong> Cet article a une vocation
            informative. Les prix, délais et pratiques mentionnés reposent sur les données
            professionnelles du secteur observées en 2026 et sont susceptibles d'évoluer.
            Les demandes de devis sont transmises uniquement à des entreprises partenaires
            ou autorisées à les recevoir.
          </p>
        </div>
      </div>
    </article>
  );
}

function injectSchema(post) {
  document.querySelectorAll('script[data-blog-schema]').forEach((n) => n.remove());
  const url = `${window.location.origin}/blog/${post.slug}`;
  const graph = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${window.location.origin}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${window.location.origin}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
    {
      "@type": "Article",
      headline: post.title,
      description: post.meta_description,
      datePublished: post.published_at,
      author: { "@type": "Organization", name: "Déménageur Pro" },
      publisher: { "@type": "Organization", name: "Déménageur Pro", url: window.location.origin },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    },
  ];
  if (post.faq?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: post.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-blog-schema", "1");
  s.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  document.head.appendChild(s);
}
